/**
 * Facility Subscription Service
 * Handles API calls for facility subscription pricing and billing statements
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get facility subscription configuration
 * @param {string} facilityId - The facility ID
 * @returns {Promise<object|null>} Subscription configuration
 */
export async function getFacilitySubscription(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/subscription`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data?.subscription || null;
  } catch (error) {
    // If 404, subscription doesn't exist yet - return null
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Error getting facility subscription:', error);
    throw error;
  }
}

/**
 * Save facility subscription configuration
 * @param {string} facilityId - The facility ID
 * @param {object} subscriptionData - The subscription configuration to save
 * @returns {Promise<object>} Save result
 */
export async function saveFacilitySubscription(facilityId, subscriptionData) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${API_BASE}/facility/facilities/${facilityId}/subscription`,
      subscriptionData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error saving facility subscription:', error);
    throw error;
  }
}

/**
 * Get the count of provider users for a facility
 * Provider roles include: doctor, nurse, attending_physician, pharmacist, etc.
 * @param {string} facilityId - The facility ID
 * @returns {Promise<number>} Provider count
 */
export async function getFacilityProviderCount(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/providers/count`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data?.count || 0;
  } catch (error) {
    console.error('Error getting facility provider count:', error);
    // Return 0 on error to allow the UI to function
    return 0;
  }
}

/**
 * Get list of monthly billing statements for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Array of statement summaries
 */
export async function getFacilityStatements(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/statements`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data?.statements || [];
  } catch (error) {
    console.error('Error getting facility statements:', error);
    throw error;
  }
}

/**
 * Get detailed statement for a specific month
 * @param {string} facilityId - The facility ID
 * @param {string} month - Month in YYYY-MM format
 * @returns {Promise<object|null>} Statement details with line items
 */
export async function getFacilityStatement(facilityId, month) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/statements/${month}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data?.statement || null;
  } catch (error) {
    console.error('Error getting facility statement:', error);
    throw error;
  }
}

/**
 * Generate a statement for a specific month (admin only)
 * @param {string} facilityId - The facility ID
 * @param {string} month - Month in YYYY-MM format
 * @returns {Promise<object>} Generated statement
 */
export async function generateFacilityStatement(facilityId, month) {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${API_BASE}/facility/facilities/${facilityId}/statements`,
      { month },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data?.statement || null;
  } catch (error) {
    console.error('Error generating facility statement:', error);
    throw error;
  }
}

/**
 * Finalize a statement (make it permanent)
 * @param {string} facilityId - The facility ID
 * @param {string} month - Month in YYYY-MM format
 * @returns {Promise<object>} Updated statement
 */
export async function finalizeFacilityStatement(facilityId, month) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${API_BASE}/facility/facilities/${facilityId}/statements/${month}/finalize`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error finalizing facility statement:', error);
    throw error;
  }
}

/**
 * Calculate projected monthly cost based on subscription config
 * This is a client-side calculation for preview purposes
 * @param {object} subscription - Subscription configuration
 * @param {number} providerCount - Number of providers
 * @returns {object} Cost breakdown
 */
export function calculateMonthlyCost(subscription, providerCount) {
  let baseAmount = 0;

  if (subscription.pricingType === 'flat') {
    baseAmount = Number(subscription.flatRateAmount) || 0;
  } else {
    const effectiveCount = subscription.providerCountOverride ?? providerCount;
    baseAmount = effectiveCount * (Number(subscription.perProviderRate) || 0);
  }

  // Calculate add-ons total
  const addOnsAmount = Object.entries(subscription.addOns || {}).reduce((total, [, config]) => {
    if (config?.enabled) {
      return total + (Number(config.price) || 0);
    }
    return total;
  }, 0);

  const subtotal = baseAmount + addOnsAmount;
  const discountAmount = subtotal * ((Number(subscription.discountPercentage) || 0) / 100);
  const afterDiscount = subtotal - discountAmount;
  const taxAmount = afterDiscount * (Number(subscription.taxRate) || 0);
  const totalAmount = afterDiscount + taxAmount;

  return {
    baseAmount,
    addOnsAmount,
    subtotal,
    discountAmount,
    afterDiscount,
    taxAmount,
    totalAmount
  };
}

// Default export with all functions
const facilitySubscriptionService = {
  getFacilitySubscription,
  saveFacilitySubscription,
  getFacilityProviderCount,
  getFacilityStatements,
  getFacilityStatement,
  generateFacilityStatement,
  finalizeFacilityStatement,
  calculateMonthlyCost
};

export default facilitySubscriptionService;
